import { ref, computed, watch } from "@vue/composition-api";
import { useToast } from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import moment from "moment-timezone";
import store from "@/store";
import { useUtils as useI18nUtils } from "@core/libs/i18n";
import { debounce } from "lodash";
import useFilterCounter from '@/components/Filters/useFiltersCount'

export default function useList() {
  const { t } = useI18nUtils();
  const trans = {
    "messages.error.server_error": t("messages.error.server_error"),
  };
  const refListTable = ref(null);
  const toast = useToast();
  const tableColumns = [
    { label: "totem_logs.data", key: "data", sortable: false },
    { label: "totem_logs.host", key: "host", sortable: true },
    { label: "totem_logs.response", key: "response", sortable: false },
    {
      label: "date.created_at",
      key: "created_at",
      sortable: true,
      formatter: (value, key, item) => {
        return moment.utc(value).tz(moment.tz.guess()).format("DD/MM/YYYY HH:mm:ss");
      },
    },
    {
      label: "date.updated_at",
      key: "updated_at",
      sortable: true,
      formatter: (value, key, item) => {
        return moment.utc(value).tz(moment.tz.guess()).format("DD/MM/YYYY HH:mm:ss");
      },
    },
  ];

  const perPage = ref(10);
  const totalList = ref(0);
  const currentPage = ref(1);
  const perPageOptions = [10, 25, 50, 100];
  const searchQuery = ref("");
  const sortBy = ref("updated_at");
  const isSortDirDesc = ref(true);
  const activeFilter = ref(null);
  const counterFilter = ref(null);
  const startDate = ref(null);
  const endDate = ref(null);

  const action = ref("list");

  const dataMeta = computed(() => {
    const localItemsCount = refListTable.value ? refListTable.value.localItems.length : 0;
    return {
      from: perPage.value * (currentPage.value - 1) + (localItemsCount ? 1 : 0),
      to: perPage.value * (currentPage.value - 1) + localItemsCount,
      of: totalList.value,
    };
  });

  const fetchList = (ctx, callback) => {

    store.dispatch("totem_logs/getAll", {
      q: searchQuery.value,
      // offset: (currentPage.value - 1) * perPage.value,
      size: perPage.value,
      page: currentPage.value,
      sortBy: sortBy.value || 'updated_at',
      sortDesc: isSortDirDesc.value ? 1 : 0,
      active: activeFilter.value,
      counter: counterFilter.value,
      updatedAtMin: startDate.value ? moment(startDate.value).format("YYYY-MM-DDTHH:mm") : null,
      updatedAtMax: endDate.value ? moment(endDate.value).format("YYYY-MM-DDTHH:mm") : null,
      action: action.value || 'list',
    })
      .then((response) => {
        if (action.value == "export") {
          action.value = "list";
        } else {
          callback(response.data);
          totalList.value = response.meta.total;
        }
      })
      .catch(() => {
        toast({
          component: ToastificationContent,
          props: {
            title: trans["messages.error.server_error"],
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
      });
  };

  const refetchData = () => {
    refListTable.value.refresh();
  };

  watch([currentPage, perPage], () => {
    refetchData();
  });

  watch([searchQuery], debounce(() => {
    refetchData()
  }, 500))

  // filters counter ============================
  const filters = ref({});
  const filtersCount = useFilterCounter(filters);
  const buildFilters = () => {
    filters.value = {
      active: activeFilter.value,
      counter: counterFilter.value,
      ...(startDate.value ? { startDate: moment(startDate.value).format("YYYY-MM-DDTHH:mm") } : {}),
      ...(endDate.value ? { endDate: moment(endDate.value).format("YYYY-MM-DDTHH:mm") } : {}),
    };
    refetchData();
  };
  const clearSearchFilters = () => {
    activeFilter.value = null;
    counterFilter.value = null;
    startDate.value = null;
    endDate.value = null;
    buildFilters();
  };

  return {
    tableColumns,
    perPage,
    currentPage,
    totalList,
    fetchList,
    dataMeta,
    perPageOptions,
    searchQuery,
    sortBy,
    isSortDirDesc,
    refListTable,
    counterFilter,
    activeFilter,
    startDate,
    endDate,
    refetchData,
    action,
    buildFilters,
    clearSearchFilters,
    filtersCount,
  };
}
